<template>
  <div
    class="selection-shadow relative w-full cursor-pointer overflow-hidden rounded-[12px] bg-white px-4 py-[10px] transition-colors after:pointer-events-none after:absolute after:inset-0 after:z-[5] after:rounded-[12px] after:border after:content-[''] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-800"
    :class="
      selected
        ? 'after:border-gray-800'
        : 'after:border-[#ebebeb] hover:bg-[#f9f9fa]'
    "
    role="radio"
    tabindex="0"
    :aria-checked="selected"
    @click="handleClick"
    @keydown.enter.prevent="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <PlanCallout v-if="priceCallout || plan.name === 'Pro Bundle'">
      {{ priceCallout || 'Big discounts as you grow!' }}
    </PlanCallout>

    <div class="text-[14px] font-normal leading-[22px] text-gray-800">
      {{ displayName }}
    </div>

    <div
      v-if="primaryPrice"
      class="flex items-baseline gap-x-[3px] text-gray-800"
    >
      <span class="text-[22px] font-medium leading-[28px]">
        {{ primaryPrice.renderedPrice }}
      </span>
      <span
        v-if="primarySuffix?.label"
        class="text-[15px] font-medium italic leading-[25px]"
      >
        {{ primarySuffix.label }}
      </span>
      <span
        v-if="primarySuffix?.fine"
        class="text-[12px] font-normal italic leading-[17px]"
      >
        {{ primarySuffix.fine }}
      </span>
    </div>

    <div
      v-if="secondaryPrice"
      class="flex items-baseline gap-x-[3px] text-gray-800"
    >
      <span class="text-[18px] font-medium leading-[22px]">
        {{ secondaryPrice.renderedPrice }}
      </span>
      <span
        v-if="secondarySuffix?.label"
        class="text-[15px] font-medium italic leading-[20px]"
      >
        {{ secondarySuffix.label }}
      </span>
      <span
        v-if="secondarySuffix?.fine"
        class="text-[12px] font-normal italic leading-[17px]"
      >
        {{ secondarySuffix.fine }}
      </span>
    </div>

    <template v-if="headerText">
      <div class="-mx-4 mt-[10px] h-px bg-black/[0.08]" />
      <div
        class="mt-[6px] text-[13px] font-normal leading-[18px] text-gray-600"
      >
        {{ headerText }}
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import {
  MembershipTier,
  PlanItemStatus,
  PlanPriceType,
  type Plan,
  type PlanPriceDetail,
} from '@/shared/graphql/legacy/generated'
import { splitFine } from '@/features/billing/utils/fine'
import { t } from '@/shared/i18n'
import PlanCallout from './PlanCallout.vue'

const props = defineProps<{
  modelValue?: Plan
  plan: Plan
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', plan: Plan): void
}>()

const selected = computed(() => props.modelValue?.id === props.plan.id)

const displayName = computed(() =>
  props.plan.membershipTier === MembershipTier.Basic ||
  props.plan.membershipTier === MembershipTier.Pro
    ? `Dacr ${props.plan.name.replace(/^Dacr\s+/, '')}`
    : props.plan.name
)

const lowestActivePlanItem = computed(() =>
  props.plan.items.find(
    (item, index, arr) =>
      item.status === PlanItemStatus.Active || index === arr.length - 1
  )
)

const subscriptionPrice = computed(
  () =>
    lowestActivePlanItem.value?.prices.find(
      (price) => price.type === PlanPriceType.Subscription
    )?.details
)

const filePrice = computed(
  () =>
    lowestActivePlanItem.value?.prices.find(
      (price) => price.type === PlanPriceType.File
    )?.details
)

// Plan copy arrives from the gateway as translation keys.
const priceCallout = computed(() => t(props.plan.priceCallout))

const headerText = computed(() => t(props.plan.headerText))

const hasSubscriptionFee = computed(() => !!subscriptionPrice.value?.amount)

const primaryPrice = computed(() =>
  hasSubscriptionFee.value
    ? subscriptionPrice.value
    : (filePrice.value ?? subscriptionPrice.value)
)

const secondaryPrice = computed(() =>
  hasSubscriptionFee.value ? filePrice.value : undefined
)

const toSuffix = (details?: PlanPriceDetail | null) => {
  const suffix = t(details?.suffix)
  return suffix ? splitFine(suffix) : null
}

const primarySuffix = computed(() => toSuffix(primaryPrice.value))

const secondarySuffix = computed(() => toSuffix(secondaryPrice.value))

const handleClick = () => {
  emit('update:modelValue', props.plan)
}
</script>

<style scoped>
.selection-shadow {
  box-shadow:
    0 0.8px 2.925px 0 rgba(0, 0, 0, 0.02),
    0 2.025px 7.8px 0 rgba(0, 0, 0, 0.004),
    0 4px 18px 0 rgba(0, 0, 0, 0.04);
}
</style>
